import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Scissors, Eye, EyeOff, Clock } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const CLINIC_IMG =
  'https://images.unsplash.com/photo-1782397132123-0166b524d6bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const BENEFITS = [
  {
    icon: Scissors,
    title: 'Entnahme in voller Länge',
    text: 'Jedes Graft wird mitsamt Langhaar einzeln entnommen — ohne dass Entnahme- oder Empfängerbereich rasiert werden. Das erfordert höchste Präzision und Erfahrung.',
  },
  {
    icon: Eye,
    title: 'Sofortiger optischer Effekt',
    text: 'Die verpflanzten Haare kaschieren den behandelten Bereich vom ersten Tag an. Sie sehen Ihre neue Haarlinie direkt in der finalen Länge.',
  },
  {
    icon: EyeOff,
    title: 'Maximale Diskretion',
    text: 'Kein rasierter Kopf, keine sichtbaren Spuren. Ideal für Berufe mit Kundenkontakt, Führungsverantwortung oder Öffentlichkeit.',
  },
  {
    icon: Clock,
    title: 'Keine Ausfallzeit',
    text: 'Ihr Alltag geht praktisch nahtlos weiter. Am nächsten Tag sind Sie gesellschaftsfähig — ohne Mütze, ohne Ausreden.',
  },
];

export const MethodSticky = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-method"
      className={`relative py-24 md:py-32 px-6 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div className="lg:sticky lg:top-28 h-fit">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm uppercase tracking-[0.25em] text-[#7BA7C2]"
          >
            Die Methode
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className={`mt-4 text-4xl sm:text-5xl font-extralight leading-tight ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Unsichtbar behandelt.
            <br />
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              Sichtbar souverän.
            </span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mt-10 rounded-2xl overflow-hidden shadow-2xl shadow-[#7BA7C2]/20"
          >
            <img
              src={CLINIC_IMG}
              alt="Moderne Klinik-Räume der Dion Hair Clinic für Long Hair FUE Haartransplantation"
              className="w-full h-72 lg:h-96 object-cover"
            />
          </motion.div>
        </div>

        <div className="space-y-6">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, x: 48 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              data-testid={`lhf-benefit-${i}`}
              className={`p-8 rounded-2xl border backdrop-blur-xl transition-colors duration-300 ${
                isDark
                  ? 'bg-slate-950/60 border-slate-800 hover:border-[#7BA7C2]/50'
                  : 'bg-slate-50/80 border-slate-200 hover:border-[#7BA7C2]/60'
              }`}
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7BA7C2] to-[#9BBFD9] flex items-center justify-center mb-5">
                <b.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className={`text-xl font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {b.title}
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {b.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const STEPS = [
  { nr: '01', title: 'Analyse & Planung', text: 'Kostenlose Analyse von Haardichte, Spenderbereich und Haarlinie. Wir planen ehrlich, was möglich ist.' },
  { nr: '02', title: 'Entnahme ohne Rasur', text: 'Die Grafts werden einzeln in voller Haarlänge aus dem Spenderbereich entnommen — unsichtbar unter dem Deckhaar.' },
  { nr: '03', title: 'Präzise Kanalöffnung', text: 'Mit feinen Saphir-Klingen entstehen exakte Kanäle in natürlicher Wuchsrichtung und -dichte.' },
  { nr: '04', title: 'Einsetzen der Langhaar-Grafts', text: 'Jedes Graft wird im richtigen Winkel platziert. Sie sehen das Ergebnis Ihrer neuen Haarlinie sofort.' },
  { nr: '05', title: 'Am nächsten Tag gesellschaftsfähig', text: 'Der behandelte Bereich ist optisch kaschiert. 12 Monate Nachbetreuung begleiten Sie bis zum Endergebnis.' },
];

export const ProcessTimeline = () => {
  const { isDark } = useTheme();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.7', 'end 0.6'] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={ref}
      data-testid="lhf-timeline"
      className={`relative py-24 md:py-36 px-6 ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}
    >
      <div className="max-w-4xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className={`text-4xl sm:text-5xl font-extralight text-center mb-20 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          So läuft Ihre <span className="text-[#7BA7C2]">Behandlung</span> ab
        </motion.h2>

        <div className="relative">
          <div
            className={`absolute left-6 md:left-1/2 top-0 bottom-0 w-px ${
              isDark ? 'bg-slate-800' : 'bg-slate-200'
            }`}
          />
          <motion.div
            style={{ scaleY: lineScale }}
            className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#7BA7C2] to-[#9BBFD9] origin-top"
          />

          <div className="space-y-16">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.nr}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-15%' }}
                transition={{ duration: 0.7 }}
                data-testid={`lhf-step-${i}`}
                className={`relative pl-16 md:pl-0 md:w-[calc(50%-3rem)] ${
                  i % 2 === 1 ? 'md:ml-auto md:text-left' : 'md:text-right'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 rounded-full bg-gradient-to-br from-[#7BA7C2] to-[#9BBFD9] ring-4 ${
                    isDark ? 'ring-slate-950' : 'ring-slate-50'
                  } left-[17px] md:left-auto ${i % 2 === 1 ? 'md:-left-[57px]' : 'md:-right-[57px]'}`}
                />
                <span
                  className="text-5xl font-thin text-[#7BA7C2]/40"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {s.nr}
                </span>
                <h3 className={`mt-2 text-xl font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {s.title}
                </h3>
                <p className={`mt-2 text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {s.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
